import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Shield, ToggleLeft, ToggleRight, ArrowLeft } from 'lucide-react';
import { usePldRules, useUpdatePldRule, useDeletePldRule, PldRule, getSeverityLabel, getSeverityColor, getRuleTypeLabel } from '../../../hooks/usePld';

const severityBadgeClasses: Record<string, string> = {
  red: 'bg-red-500/20 text-red-400',
  orange: 'bg-orange-500/20 text-orange-400',
  blue: 'bg-blue-500/20 text-blue-400',
  yellow: 'bg-yellow-500/20 text-yellow-400',
  gray: 'bg-surface-600 text-surface-300',
};

export function PldRuleList() {
  const { data: rules, isLoading } = usePldRules();
  const updateMutation = useUpdatePldRule();
  const deleteMutation = useDeletePldRule();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleToggle = async (rule: PldRule) => {
    await updateMutation.mutateAsync({ id: rule.id, isActive: !rule.isActive });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta regra?')) return;
    setDeletingId(id);
    try {
      await deleteMutation.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Regras PLD</h1>
          <p className="text-surface-400">Configure as regras de detecção de atividades suspeitas</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/compliance/pld" className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
          <Link to="/admin/compliance/pld/rules/new" className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> Nova Regra
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !rules?.length ? (
          <div className="p-12 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-surface-600" />
            <p className="text-surface-400">Nenhuma regra configurada</p>
            <Link to="/admin/compliance/pld/rules/new" className="inline-flex items-center gap-2 mt-4 text-sm text-primary-400 hover:text-primary-300">
              <Plus className="w-4 h-4" /> Criar primeira regra
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Severidade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {rules.map((rule: PldRule) => (
                <tr key={rule.id} className="hover:bg-surface-800/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium">{rule.name}</div>
                    {rule.description && <div className="text-sm text-surface-500 mt-0.5">{rule.description}</div>}
                  </td>
                  <td className="px-6 py-4 text-sm text-surface-300">{getRuleTypeLabel(rule.type)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${severityBadgeClasses[getSeverityColor(rule.severity)] || severityBadgeClasses.gray}`}>
                      {getSeverityLabel(rule.severity)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggle(rule)}
                      className={`flex items-center gap-1.5 text-sm transition-colors ${rule.isActive ? 'text-green-400 hover:text-green-300' : 'text-surface-500 hover:text-surface-400'}`}
                    >
                      {rule.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      {rule.isActive ? 'Ativa' : 'Inativa'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/compliance/pld/rules/${rule.id}`} className="p-2 text-surface-400 hover:text-primary-400 transition-colors">
                        <Edit className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(rule.id)}
                        disabled={deletingId === rule.id}
                        className="p-2 text-surface-400 hover:text-red-400 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PldRuleList;
